import type {
  InstrumentForgeProfile, PhysicsLayers, MixTarget, ProfileKeywords,
} from '../types/instrumentForge.types';

/**
 * Brings any InstrumentForge profile to the full shape the blender, the physics formatter and the cards read
 * (AUTO-102). Profiles made by the generator (/api/instrument-packs/generate, "Build the five Forges") are
 * stored in a simpler shape than the built-in ones: no `identity`, `mixTargets` as plain strings, `specialty`
 * as one object, bass `frequencySplit` instead of `split`, and each physics layer as ONE string of concatenated
 * "[Tag: a; b]" bracket tags instead of per-field objects. Selecting one in a mid-range blend threw
 * "Cannot read properties of undefined (reading 'replace')", and selecting one alone silently printed no
 * physics text at all.
 *
 * Every field the profile already has is kept (the original is spread first); only gaps are filled and the
 * generator's shape is converted. Safe to call on a built-in profile or on an already-normalized one.
 */

const HEADERS = {
  layer2: '{LAYER-2 RHYTHM SECTION PHYSICS}',
  layer3: '{LAYER-3 GUITAR PHYSICS}',
  layer4: '{LAYER-4 ATMOSPHERE PHYSICS}',
  layer5: '{LAYER-5 SPECIALTY PHYSICS}',
};

const TRAITS: Array<keyof ProfileKeywords> = ['weight', 'clarity', 'saturation', 'roomSize', 'tightness', 'warmth', 'aggression', 'vintage'];

type Rec = Record<string, any>;
const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const rec = (v: unknown): Rec => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Rec) : {});
const hasContent = (o: Rec): boolean => Object.values(o).some(v => (typeof v === 'string' ? v.trim() !== '' : v != null));

/** The first "[Label: ...]" tag in a layer paragraph, brackets kept (that is how built-ins store them). */
export function extractPhysicsTag(layer: string, label: string): string {
  const m = layer.match(new RegExp(`\\[\\s*${label}\\s*:[^\\]]*\\]`, 'i'));
  return m ? m[0].trim() : '';
}

/** A layer is either already an object (built-ins) or one paragraph of bracket tags (generator). */
function layerFields(raw: unknown, fields: Array<[field: string, label: string]>): Rec {
  if (typeof raw === 'string') {
    const out: Rec = {};
    let found = false;
    for (const [field, label] of fields) {
      out[field] = extractPhysicsTag(raw, label);
      if (out[field]) found = true;
    }
    // A paragraph with no recognizable tags is kept whole in the first field rather than dropped.
    if (!found && raw.trim()) out[fields[0][0]] = raw.trim();
    return out;
  }
  const o = rec(raw);
  const out: Rec = { ...o };
  for (const [field] of fields) out[field] = str(o[field]);
  return out;
}

function normalizeLayers(raw: unknown): PhysicsLayers {
  const o = rec(raw);
  const l2 = layerFields(o.layer2, [['drumBehavior', 'Drum_Behavior'], ['drumChain', 'Drum_Chain'], ['bassBehavior', 'Bass_Behavior'], ['bassChain', 'Bass_Chain']]);
  const l3 = layerFields(o.layer3, [['guitarBehavior', 'Guitar_Behavior'], ['rhythmLChain', 'Rhythm_L_Chain'], ['rhythmRChain', 'Rhythm_R_Chain'], ['leadChain', 'Lead_Chain'], ['textureChain', 'Texture_Chain']]);
  const out: Rec = {
    layer2: { ...l2, header: str(l2.header) || HEADERS.layer2 },
    // leadChain / textureChain are optional in the type: keep them only when present.
    layer3: { ...l3, header: str(l3.header) || HEADERS.layer3 },
  };
  if (!out.layer3.leadChain) delete out.layer3.leadChain;
  if (!out.layer3.textureChain) delete out.layer3.textureChain;

  if (o.layer4 != null && (typeof o.layer4 === 'string' ? o.layer4.trim() : hasContent(rec(o.layer4)))) {
    const l4 = layerFields(o.layer4, [['keysBehavior', 'Keys_Behavior'], ['keysChain', 'Keys_Chain']]);
    out.layer4 = { ...l4, header: str(l4.header) || HEADERS.layer4 };
  }
  if (o.layer5 != null && (typeof o.layer5 === 'string' ? o.layer5.trim() : hasContent(rec(o.layer5)))) {
    const l5 = layerFields(o.layer5, [['instrumentBehavior', 'Instrument_Behavior'], ['instrumentChain', 'Instrument_Chain']]);
    out.layer5 = { ...l5, header: str(l5.header) || HEADERS.layer5 };
  }
  return out as PhysicsLayers;
}

function normalizeMixTargets(raw: unknown): MixTarget[] {
  if (!Array.isArray(raw)) return [];
  const out: MixTarget[] = [];
  for (const t of raw) {
    if (typeof t === 'string') { if (t.trim()) out.push({ goal: t, details: '' }); }
    else if (t && typeof t === 'object') out.push({ ...(t as Rec), goal: str((t as Rec).goal), details: str((t as Rec).details) } as MixTarget);
  }
  return out;
}

function strings(raw: unknown, fields: string[]): Rec {
  const o = rec(raw);
  const out: Rec = { ...o };
  for (const f of fields) out[f] = str(o[f]);
  return out;
}

function optionalSpec(raw: unknown, fields: string[]): Rec | undefined {
  const o = rec(raw);
  return hasContent(o) ? strings(o, fields) : undefined;
}

function numPair(raw: unknown, fallback: [number, number]): [number, number] {
  return Array.isArray(raw) && raw.length === 2 && raw.every(n => typeof n === 'number') ? [raw[0], raw[1]] : fallback;
}

export function normalizeInstrumentProfile(input: InstrumentForgeProfile): InstrumentForgeProfile {
  const p = rec(input);
  const name = str(p.name);
  const identity = rec(p.identity);

  const rhythm = rec(p.rhythmSection);
  const bass = rec(rhythm.bass);
  const guitars = rec(p.guitars);

  const kw = rec(p.keywords);
  const keywords: Rec = { ...kw };
  for (const t of TRAITS) keywords[t] = Array.isArray(kw[t]) ? kw[t].filter((x: unknown) => typeof x === 'string') : [];

  const specialtyRaw = p.specialty;
  const specialty = Array.isArray(specialtyRaw)
    ? specialtyRaw.filter(s => s && typeof s === 'object').map(s => strings(s, ['name', 'character', 'role']))
    : hasContent(rec(specialtyRaw)) ? [strings(specialtyRaw, ['name', 'character', 'role'])] : [];

  const out: Rec = {
    ...p,
    id: str(p.id),
    name,
    description: str(p.description),
    excludeStyles: str(p.excludeStyles),
    // Never invented here: 20 built-ins have an entirely empty identity, indistinguishable from a generated one.
    // The profile name stands in at the output step instead (profileBlender.ts), where it only replaces blank text.
    identity: { ...identity, era: str(identity.era), movement: str(identity.movement), tone: str(identity.tone), attitude: str(identity.attitude) },
    rhythmSection: {
      ...rhythm,
      drums: strings(rhythm.drums, ['kit', 'room', 'character', 'compression', 'chain']),
      // the generator calls the split "frequencySplit"
      bass: { ...strings(bass, ['instrument', 'lock', 'character', 'chain']), split: str(bass.split) || str(bass.frequencySplit) },
    },
    guitars: {
      ...guitars,
      rhythmLeft: strings(guitars.rhythmLeft, ['character', 'chain', 'role']),
      rhythmRight: strings(guitars.rhythmRight, ['character', 'chain', 'role']),
      lead: optionalSpec(guitars.lead, ['character', 'chain', 'role']),
      texture: optionalSpec(guitars.texture, ['character', 'chain', 'role']),
    },
    keysAtmosphere: optionalSpec(p.keysAtmosphere, ['instrument', 'character', 'role', 'behavior']),
    specialty,
    mixTargets: normalizeMixTargets(p.mixTargets),
    physicsLayers: normalizeLayers(p.physicsLayers),
    genres: Array.isArray(p.genres) ? p.genres.filter((g: unknown) => typeof g === 'string') : [],
    tempoRange: numPair(p.tempoRange, [80, 140]),
    preferredKeys: Array.isArray(p.preferredKeys) ? p.preferredKeys.filter((k: unknown) => typeof k === 'string') : [],
    energyRange: numPair(p.energyRange, [5, 8]),
    keywords,
  };
  // Optional fields stay absent when the profile has nothing for them (matches the built-ins).
  if (!out.guitars.lead) delete out.guitars.lead;
  if (!out.guitars.texture) delete out.guitars.texture;
  if (!out.keysAtmosphere) delete out.keysAtmosphere;
  if (!out.rhythmSection.bass.split) delete out.rhythmSection.bass.split;
  for (const k of ['chain']) {
    if (!out.rhythmSection.drums[k]) delete out.rhythmSection.drums[k];
    if (!out.rhythmSection.bass[k]) delete out.rhythmSection.bass[k];
  }
  return out as unknown as InstrumentForgeProfile;
}

/** Normalizes every profile in a keyed map (the shape the profiles API and hook return). */
export function normalizeInstrumentProfiles(map: Record<string, InstrumentForgeProfile>): Record<string, InstrumentForgeProfile> {
  const out: Record<string, InstrumentForgeProfile> = {};
  for (const [k, v] of Object.entries(map)) out[k] = normalizeInstrumentProfile(v);
  return out;
}
